import { FC } from 'react'
import { useQueryClient } from 'react-query'
import Swal from 'sweetalert2'
import { RowActions, useModal, useToast } from '../../../../../../components'
import {
  downloadRelatorioTeleconsultoria,
  getAtendimentoById,
} from '../../../../atendimento/core/_requests'
import {
  downloadArquivo,
  IconPaths,
  showAlertLoading,
  showError,
  SituacaoInterconsulta,
} from '../../../../../../../_metronic/helpers'
import { AtendimentoView } from '../../../../atendimento/core/_models'
import { SelecionarFormularioModal } from '../../../../atendimento_medicos/atendimento-video/modal/SelecionarFormularioModal'
import { FormularioModal } from '../../../../atendimento_medicos/atendimento-video/modal/formulario-answer/FormularioModal'
import {
  getFormularioAtendimento,
  getFormularioResolvido,
} from '../../../../avaliacoes/avaliacoes-list/core/_requests'
import { InterconsultaSolicitanteList } from '../../core/_models'
import { acoesSolicitante, teleconsultoriaDisponivel } from '../../core/situacaoInterconsulta'
import { ReplicaModal } from '../../components/modal/ReplicaModal'
import { DesfechoModal } from '../../components/modal/DesfechoModal'
import { ResolverPendenciaModal } from '../../components/modal/ResolverPendenciaModal'
import { AnexosInterconsultaModal } from '../../components/modal/AnexosInterconsultaModal'
import { enviarFormularioSolicitacao } from '../../core/_requests'
import { useEscopoPareceres } from '../../core/escopoPareceres'
import { formulariosEmAberto } from '../../core/formulariosInterconsulta'
import { FormularioAtendimentoList } from '../../../../avaliacoes/avaliacoes-pendentes-list/core/_models'

type Props = {
  atendimento: InterconsultaSolicitanteList
}

export const PareceresActionsCell: FC<Props> = ({ atendimento: linha }) => {
  const { showModal } = useModal()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  // Na lista da equipe a linha pode ser de outro solicitante: réplica, desfecho e complemento são
  // só dele (o backend confere a identidade), então ali a linha é só de acompanhamento.
  const daEquipe = useEscopoPareceres() === 'equipe'
  const atendimentoId = Number(linha.id)
  const paciente = linha.paciente?.pessoa?.nome ?? ''
  const nome = `Parecer de ${paciente}`.trim()
  const acoes = daEquipe
    ? { resolverPendencia: false, replicar: false, avaliar: false }
    : acoesSolicitante(linha)
  // O solicitante altera a solicitação (formulário e anexos) só enquanto ela aguarda o
  // teleconsultor: depois que ele assume, responde ou há réplica, o backend recusa com 422.
  const podeComplementar =
    !daEquipe && linha.situacaoInterconsulta === SituacaoInterconsulta.Aguardando

  // Atendimento completo (com os formulários): buscado no clique, em cache por alguns segundos
  // para as ações seguintes da mesma linha.
  const carregarAtendimento = (): Promise<AtendimentoView> =>
    queryClient.fetchQuery(
      ['parecer-atendimento', atendimentoId],
      () => getAtendimentoById(atendimentoId),
      { staleTime: 30_000 }
    )

  const baixarTeleconsultoria = async () => {
    try {
      showAlertLoading()
      const arquivo = await downloadRelatorioTeleconsultoria(atendimentoId)
      Swal.close()
      downloadArquivo(arquivo)
    } catch (error) {
      showError(error)
    }
  }

  const openAvaliacao = (avaliacao: FormularioAtendimentoList) =>
    showModal({
      title: avaliacao.formulario.nome,
      content: (
        <FormularioModal atendimentoId={atendimentoId} enviar={enviarFormularioSolicitacao} />
      ),
      fetchers: {
        formularioAtendimento: () => getFormularioAtendimento(atendimentoId, avaliacao.id),
        formularioResolvido: () => getFormularioResolvido(atendimentoId, avaliacao.id),
      },
    })

  const editarAvaliacao = async () => {
    try {
      // O formulário da interconsulta (sem marcado, todos). Assinado não se edita: depois de uma
      // réplica, a instância anterior fica de fora.
      const formularios = formulariosEmAberto((await carregarAtendimento()).formularios)
      if (!formularios.length) {
        showToast({ variant: 'info', title: 'Este atendimento não tem avaliações.' })
        return
      }
      if (formularios.length === 1) return openAvaliacao(formularios[0])

      showModal({
        title: 'Selecionar avaliação',
        content: <SelecionarFormularioModal formularios={formularios} onSelect={openAvaliacao} />,
      })
    } catch (error) {
      showError(error)
    }
  }

  // Mesmo modal para anexar (enquanto aguarda) e só consultar/baixar (nos demais casos).
  const abrirAnexos = () =>
    showModal({
      title: `Exames de ${paciente} - ${linha.protocolo ?? ''}`,
      content: (
        <AnexosInterconsultaModal atendimentoId={atendimentoId} editavel={podeComplementar} />
      ),
    })

  const resolverPendencia = () =>
    showModal({
      title: `Pendência — ${linha.protocolo ?? paciente}`,
      content: <ResolverPendenciaModal atendimentoId={atendimentoId} />,
      fetchers: { atendimento: () => getAtendimentoById(atendimentoId) },
    })

  const replicar = () =>
    showModal({
      title: `Replicar parecer — ${linha.protocolo ?? paciente}`,
      content: (
        <ReplicaModal
          atendimentoId={atendimentoId}
          prazoReplica={linha.interconsulta?.prazoReplica}
        />
      ),
    })

  const registrarDesfecho = () =>
    showModal({
      title: `Desfecho da interconsulta — ${linha.protocolo ?? paciente}`,
      content: (
        <DesfechoModal
          atendimentoId={atendimentoId}
          prazoAvaliacao={linha.interconsulta?.prazoAvaliacao}
        />
      ),
    })

  // O parecer chega ao solicitante pelo relatório da teleconsultoria (PDF), disponível com o parecer
  // assinado. Pendência bloqueia o fluxo: quando há, ela é a ação principal e o PDF vai para o menu.
  const parecerDisponivel = teleconsultoriaDisponivel(linha.situacaoInterconsulta)
  const baixarAction = {
    label: 'Baixar relatório da teleconsultoria',
    onClick: baixarTeleconsultoria,
    icon: IconPaths.Download,
  }
  const primaryAction = acoes.resolverPendencia
    ? { label: 'Resolver pendência', onClick: resolverPendencia }
    : parecerDisponivel
    ? baixarAction
    : undefined

  return (
    <RowActions
      itemName={nome}
      primaryAction={primaryAction}
      items={[
        { ...baixarAction, hidden: !parecerDisponivel || primaryAction === baixarAction },
        { label: 'Replicar', onClick: replicar, hidden: !acoes.replicar },
        { label: 'Registrar desfecho', onClick: registrarDesfecho, hidden: !acoes.avaliar },
        {
          label: 'Editar avaliação',
          onClick: editarAvaliacao,
          hidden: !podeComplementar,
        },
        { label: podeComplementar ? 'Anexar exames' : 'Ver exames', onClick: abrirAnexos },
      ]}
    />
  )
}
